<template>
  <d-card
    :class="[
      'card-small d-inline-flex',
      { show: show, 'open-below': openBelow },
    ]"
    :style="{ top: `${positionTop}px`, left: `${positionLeft}px` }"
  >
    <d-card-body class="px-2 py-0">
      <label
        v-if="!hasQuestionsAvailable"
        class="no-questions-label text-center mb-3"
      >
        Esta resposta não possui perguntas cadastradas!
      </label>
      <d-form-row>
        <d-col class="form-group">
          <label>Tamanho do texto</label>
          <d-select
            v-model="cell.question.size_id"
            @change="recalcQuestionLimit"
            :class="{ 'is-invalid': submitted && !cell.question.size_id }"
          >
            <option :value="null">Selecione um tamanho</option>
            <option
              :value="option.id"
              v-for="option in optionsSizes"
              :key="option.id"
            >
              {{ option.name }}
            </option>
          </d-select>
          <d-form-invalid-feedback>Campo obrigatório</d-form-invalid-feedback>
        </d-col>
        <d-col class="form-group">
          <label>Tamanho da fonte</label>
          <d-select
            v-model="cell.settings.font_size"
            :class="{ 'is-invalid': submitted && !cell.settings.font_size }"
          >
            <option :value="null">Selecione o tamanho da fonte</option>
            <option :value="n + 7" v-for="n in 5" :key="n">
              {{ n + 7 }}px
            </option>
          </d-select>
          <d-form-invalid-feedback>Campo obrigatório</d-form-invalid-feedback>
        </d-col>
      </d-form-row>
      <d-form-row v-if="hasQuestionsAvailable">
        <d-col class="form-group">
          <label class="d-flex align-items-center">
            <span>
              Perguntas cadastradas
              <small v-if="question" class="link ml-2" @click="editQuestion">
                Editar
              </small>
              <small
                v-if="question"
                class="link-delete ml-1"
                @click="openDeleteConfirmation"
              >
                Deletar
              </small>
            </span>

            <i
              id="question-level-tooltip"
              class="fas fa-info-circle text-muted ml-auto"
              style="cursor: pointer"
            />
          </label>
          <d-tooltip target="question-level-tooltip" placement="top">
            <div class="text-left">
              <p class="mb-2">Níveis de dificuldade:</p>
              <div v-for="level in LEVEL_OPTIONS" :key="level.slug">
                <i :class="[level.color, 'fas fa-circle ']" />
                {{ level.label }}
              </div>
            </div>
          </d-tooltip>
          <custom-select
            v-model="question"
            :options="questionsAvailable"
            label="question"
            @change="selectQuestion"
          >
            <template #option="{ option }">
              <i
                :class="getLevelColor(option.level)"
                class="fas fa-circle mr-2"
              />
              {{ option.question }}
            </template>
          </custom-select>
        </d-col>
      </d-form-row>
      <d-form-row v-else>
        <d-col class="form-group">
          <label>Nível</label>
          <custom-select
            v-model="cell.question.level"
            :options="LEVELS"
            placeholder="Selecione o nível..."
            :class="{ 'is-invalid': submitted && !cell.question.level }"
          >
            <template #option="{ option }">
              <i :class="getLevelColor(option)" class="fas fa-circle mr-2" />
              {{ getLevelLabel(option) }}
            </template>
            <template #selected="{ option }">
              {{ getLevelLabel(option) }}
            </template>
          </custom-select>
          <d-form-invalid-feedback>
            Nível é obrigatório
          </d-form-invalid-feedback>
        </d-col>
      </d-form-row>
      <d-form-row>
        <d-col class="form-group">
          <label>
            Pergunta
            <small v-if="cell.answer.answer">
              ( Resposta: {{ cell.answer.answer }} )
            </small>
          </label>
          <textarea
            :class="[
              'form-control',
              {
                bold: cell.settings.bold,
                'is-invalid': submitted && !cell.question.question,
              },
            ]"
            :style="{ fontSize: `${cell.settings.font_size}px` }"
            v-model="cell.question.question"
            @keydown="limitQuestion"
          />
          <d-form-invalid-feedback>
            Pergunta é obrigatória
          </d-form-invalid-feedback>
        </d-col>
      </d-form-row>
      <d-form-row>
        <d-col class="d-flex justify-content-between">
          <d-button
            type="button"
            theme="light"
            class="w-100 mr-2"
            @click="close()"
          >
            Cancelar
          </d-button>
          <d-button type="button" class="w-100 ml-2" @click="save()">
            Salvar
          </d-button>
        </d-col>
      </d-form-row>
    </d-card-body>
    <confirmation-modal
      :cancel="closeDeleteConfirmation"
      :confirm="executeDeleteQuestion"
      type-modal="puzzle_question_delete_modal"
      title="Excluir Pergunta"
      message="Tem certeza que deseja excluir esta pergunta do sistema?"
    />
  </d-card>
</template>

<script>
import Events from '_utils/events';
import { mapActions } from 'vuex';
import {
  cloneDeep,
  LEVEL_OPTIONS,
  LEVELS,
  LEVEL,
  getLevelLabel,
  getLevelColor,
} from '_utils';
import PuzzleService from '_services/rest/modules/puzzle';
import ConfirmationModal from '_shared/ConfirmationModal.vue';
import CustomSelect from '_shared/CustomSelect.vue';
import modalPositionMixin from '_common/mixins/modalPositionMixin';
import {
  CELL,
  CELL_SIZE,
  _PUZZLE_QUESTION_OPTION_,
  _PUZZLE_SET_QUESTION_,
  _PUZZLE_EDIT_QUESTION_OPTION_,
  _PUZZLE_EDIT_QUESTION_UPDATED_,
  _PUZZLE_CLOSE_ALL_MODAL_,
} from '../PuzzleModel';

const QUESTION_MODAL_OFFSET_HORIZONTAL = 150;
const QUESTION_CELL_SIZE = 20;
const QUESTION_CELL_OFFSET_TOP = 320;
const QUESTION_CELL_OFFSET_LEFT = 147;

const KEY_CODE_DIGIT_ZERO = 48;
const KEY_CODE_KEY_Z = 90;

export default {
  name: 'puzzle-option-question-modal',
  mixins: [modalPositionMixin],
  components: {
    ConfirmationModal,
    CustomSelect,
  },
  computed: {
    puzzle() {
      return this.$store.state.puzzle.puzzle || null;
    },
    optionsSizes() {
      const { questionSizes } = this.$store.state.puzzle;
      return questionSizes || [{ id: null, name: null }];
    },
    hasQuestionsAvailable() {
      return this.questionsAvailable && this.questionsAvailable.length > 0;
    },
  },
  data() {
    return {
      submitted: false,
      cell: cloneDeep(CELL),
      question: null,
      questionId: null,
      questionsAvailable: null,
      LEVELS,
      LEVEL_OPTIONS,
    };
  },
  methods: {
    ...mapActions(['setToggle', 'getQuestionSizes', 'activeAlert']),
    getLevelLabel,
    getLevelColor,
    async save() {
      this.submitted = true;

      if (!this.validate()) return;

      this.cell.isAnswer = false;
      this.cell.question.question = this.cell.question.question.trim();
      this.cell.question.answer_id = this.cell.answer.id;
      this.cell.text = this.cell.question.question;

      if (!this.cell.question.level) {
        this.cell.question.level = LEVEL.MEDIUM;
      }

      const response = await PuzzleService.saveQuestion(this.cell.question);
      this.cell.question.id = response.id;
      this.cell.question_id = response.id;
      this.cell.question.level = response.level;
      this.cell.updated = true;

      Events.$emit(_PUZZLE_SET_QUESTION_, this.cell);
      this.submitted = false;
      this.close();
    },
    validate() {
      const hasLevel = this.hasQuestionsAvailable || this.cell.question.level;
      if (
        !this.cell.question.size_id ||
        !this.cell.settings.font_size ||
        !hasLevel ||
        !this.cell.question.question
      ) {
        return false;
      }
      return true;
    },
    selectQuestion(question) {
      this.question = question;
      this.questionId = question.id;
      this.cell.question.id = question.id;
      this.cell.question.size = question.size;
      this.cell.question.size_id = question.size_id;
      this.cell.question.question = question.question;
      this.cell.question.level = question.level;
    },
    close() {
      this.question = null;
      this.cell = cloneDeep(CELL);
      this.questionId = null;
      this.show = false;
    },
    getOptions() {
      if (!this.$store.state.puzzle.questionSizes) {
        this.getQuestionSizes();
      }
      this.getQuestionsByAnswer();
    },
    getQuestionsByAnswer() {
      if (this.cell.answer.id) {
        PuzzleService.getQuestionsByAnswerId(this.cell.answer.id).then(
          response => {
            this.questionsAvailable = response;
          },
        );
      }
    },
    toggleSetting(value, setting) {
      this.cell.settings[setting] = value;
    },
    limitQuestion(e) {
      const total = this.cell.question.question
        ? this.cell.question.question.length
        : 0;
      const size = this.cell.question.size
        ? this.cell.question.size.size
        : QUESTION_CELL_SIZE;
      if (total > size) {
        this.cell.question.question.slice(0, -1);
        if (e.keyCode >= KEY_CODE_DIGIT_ZERO && e.keyCode <= KEY_CODE_KEY_Z) {
          e.preventDefault();
        }
      }
    },
    recalcQuestionLimit(event) {
      if (!event) {
        this.cell.question.size = null;
        this.cell.question.size_id = null;
        return;
      }
      const { question } = this.cell;
      this.cell.question.size_id = event;
      [this.cell.question.size] = this.optionsSizes.filter(
        op => op.id === event,
      );
      this.cell.question.question = question.question
        ? question.question.substring(0, this.cell.question.size.size)
        : null;
    },
    editQuestion() {
      this.setToggle({ type: 'puzzle_edit_question_modal', bool: true });
      Events.$emit(_PUZZLE_EDIT_QUESTION_OPTION_, this.question);
    },
    openDeleteConfirmation() {
      this.setToggle({ type: 'puzzle_question_delete_modal', bool: true });
    },
    closeDeleteConfirmation() {
      this.setToggle({ type: 'puzzle_question_delete_modal', bool: false });
    },
    clearQuestionData() {
      this.question = null;
      this.questionId = null;
      this.cell.question.id = null;
      this.cell.question.question = null;
      this.cell.question.level = LEVEL.MEDIUM;
      this.cell.question_id = null;
      this.cell.text = null;
    },
    executeDeleteQuestion() {
      PuzzleService.deleteQuestion(this.question.id).then(() => {
        this.activeAlert({
          message: 'Pergunta deletada com sucesso!',
          theme: 'success',
        });
        this.getQuestionsByAnswer();

        this.clearQuestionData();
        Events.$emit(_PUZZLE_SET_QUESTION_, this.cell);

        this.closeDeleteConfirmation();
      });
    },
  },
  destroyed() {
    this.question = null;
    Events.$off(_PUZZLE_QUESTION_OPTION_);
    Events.$off(_PUZZLE_CLOSE_ALL_MODAL_);
    Events.$off(_PUZZLE_EDIT_QUESTION_UPDATED_);
  },
  watch: {
    questionsAvailable(list) {
      if (list && this.cell.question?.id) {
        this.question =
          list.find(question => question.id === this.cell.question.id) || null;
      }
    },
  },
  mounted() {
    this.getOptions();
    this.registerModalEvents(_PUZZLE_QUESTION_OPTION_, (cell, cellRect) => {
      this.cell = cell;
      this.question = cell.question?.id ? cell.question : null;
      this.questionId = cell.question?.id || null;
      this.getQuestionsByAnswer();
      this.show = true;
      if (cellRect) {
        this.applyPosition(cellRect, QUESTION_MODAL_OFFSET_HORIZONTAL);
      } else {
        this.openBelow = false;
        this.positionTop =
          cell.positiony * CELL_SIZE - QUESTION_CELL_OFFSET_TOP;
        this.positionLeft =
          cell.positionx * CELL_SIZE +
          CELL_SIZE / 2 -
          QUESTION_CELL_OFFSET_LEFT;
      }
    });
    Events.$on(_PUZZLE_EDIT_QUESTION_UPDATED_, question => {
      Object.assign(this.question, question);
    });
  },
};
</script>

<style lang="scss" scoped>
@import '~_scss/overrides';

.no-questions-label {
  font-size: 14px;
  color: $orange;
  font-weight: bold;
}

.link {
  color: $primary;
  cursor: pointer;
}

.link-delete {
  color: $red;
  cursor: pointer;
}

.card-small {
  opacity: 0;
  pointer-events: none;
  transform: translateY(-100%) translateY(-6px);
  border-radius: 6px;
  padding: 5px 0;
  position: absolute;
  z-index: 1199;
  transition: opacity 0.2s ease, transform 0.2s ease;
  background: #d7d7d7;
  width: 300px;
  &:before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #d7d7d7 transparent transparent transparent;
  }

  &.open-below {
    transform: translateY(6px);
  }
  &.open-below:before {
    top: auto;
    bottom: 100%;
    border-color: transparent transparent #d7d7d7 transparent;
  }

  &.show {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(-100%);
  }

  &.show.open-below {
    transform: translateY(0);
  }

  .bold {
    font-weight: bold;
  }
}
</style>
